import { DangerModal, EditToolToggle } from '@carboncopyco/components';
import {
  selectOrganizationId,
  useDeleteTourThemeMutation,
  useGetTourQuery,
  useGetTourThemesQuery,
  useUpdateAllTourStepsMutation,
} from '@carboncopyco/data';
import {
  BalloonOutlineIcon,
  LandscapeHorizontalAlignOutlineIcon,
  LeftOutlineIcon,
  MapOutlineIcon,
  ResponsiveOutlineIcon,
  SettingsIcon,
  TimerOutlineIcon,
} from '@carboncopyco/icons';
import type {
  EditToolColorValues,
  IUser,
  TourFrame,
  TourSettings,
  TourTheme,
  WrapperType,
} from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  Drawer,
  InlineLink,
  RadioGroupButton,
  Separator,
  TextBoxEditorTitle,
  TextCaption,
  TextSubHeader,
} from '@carboncopyco/ui';
import {
  camelCaseToUnderscore,
  getObjectPropertyPathsWithValues,
} from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTourDispatch, useTourSelector } from '../../../../hooks';
import { useProcessImageAsFrame } from '../../../../hooks/useProcessImageAsFrame';
import {
  selectCapturedScreenSize,
  selectDraftSettings,
  setDraftSettings,
  setIsApplyingTourTheme,
  setIsSettingsModified,
} from '../../../../store';
import { DEFAULT_BACKGROUND_COLOR } from '../../../../utils';
import { getScreenResolutionTypeFromWidth } from '../../../../utils/getScreenResolutionTypeFromWidth';
import { useTourEditorPageProps } from '../../TourEditorPageContext';
import type { ReplayerEditorRef } from '../../ToursReplayerEditor';
import useFrameWarnings from '../FrameList/Frame/useFrameWarnings';
import { CoverPicker } from './Pickers/CoverPicker';
import { TourThemePicker } from './Pickers/TourTheme/TourThemePicker';
import useSettingsActions from './useSettingsActions';

interface ISettings {
  tourId: string;
  frames: TourFrame[];
  user: IUser;
  replayerRef: React.RefObject<ReplayerEditorRef>;
  isSettingsOpen: boolean;
  onSettingsClose: () => void;
  workspaceId: string;
  fileServiceToken: string;
}

const areSettingsEqual = (
  draftSettings: TourSettings,
  tourSettings: TourSettings
) => {
  const { background: draftBackground, ...restDraftSettings } = draftSettings;
  const { background: liveBackground, ...restTourSettings } = tourSettings;

  // draft background can be an object with all `null` values if we undo changes
  if (
    draftBackground &&
    Object.values(draftBackground).every((val) => val === null) &&
    liveBackground === null
  ) {
    return isEqual(restDraftSettings, restTourSettings);
  }

  return isEqual(draftSettings, tourSettings);
};

const Settings: React.FC<ISettings> = ({
  tourId,
  frames,
  user,
  isSettingsOpen,
  replayerRef,
  onSettingsClose,
  workspaceId,
  fileServiceToken,
}) => {
  const { uuid } = useTourEditorPageProps();
  const organizationId = useSelector(selectOrganizationId);

  const flags = useFlags();
  const dispatch = useTourDispatch();

  const draftSettings = useTourSelector(selectDraftSettings(uuid));

  const { data: tour } = useGetTourQuery(
    { organizationId, workspaceId, id: Number(tourId) },
    { skip: !tourId }
  );
  const { data: tourThemes } = useGetTourThemesQuery(
    { organizationId },
    { skip: !organizationId || !flags.toursOrgLevelThemes }
  );

  const tourSettings = tour?.tourSettings;

  const isCustomDimension = useMemo(
    () =>
      (draftSettings?.screenResolution?.type &&
        draftSettings?.screenResolution?.type !== 'responsive') ||
      false,
    [draftSettings]
  );

  useEffect(() => {
    if (!draftSettings || !tourSettings) {
      return;
    }
    if (areSettingsEqual(draftSettings, tourSettings)) {
      dispatch(setIsSettingsModified({ uuid, isModified: false }));
      return;
    }

    dispatch(setIsSettingsModified({ uuid, isModified: true }));
  }, [dispatch, draftSettings, tourSettings, uuid]);

  const updateDraftSettings = useSettingsActions({
    tourId: Number(tourId),
    organizationId,
    frames: frames,
    draftSettings,
    workspaceId,
  });

  const capturedScreenSize = useTourSelector(selectCapturedScreenSize(uuid));
  const screenResolutionType = getScreenResolutionTypeFromWidth(
    capturedScreenSize?.width
  );

  // Currently we are using draftSettings?.responsivenessDisabled if there is ss frame and responsivness is disabled
  // and second condition is for capturing tour in specific resolution type
  const isResponsivenessDisabled =
    !!draftSettings?.responsivenessDisabled || isCustomDimension;

  const responsiveness = !!draftSettings?.responsiveness;
  const navigationToggle = !!draftSettings?.navigationToggle;
  const wrapperType = draftSettings?.background?.wrapperType;
  const showTooltips = !!draftSettings?.showTooltips;

  const { framesWarnings, calculateAndStoreStepWarnings } = useFrameWarnings();
  const areWarningsCalculated = useRef<boolean>();

  const { fileServiceDynamicLink } = useTourEditorPageProps();
  const { uploadAnImage } = useProcessImageAsFrame({
    fileServiceDynamicLink,
  });

  /**
   * Only on initial load, calculate steps warnings
   */
  useEffect(() => {
    if (!frames?.length || !draftSettings || areWarningsCalculated.current) {
      return;
    }

    calculateAndStoreStepWarnings(frames);
    areWarningsCalculated.current = true;
  }, [frames, showTooltips, draftSettings]);

  const showStepCounter = useMemo(() => {
    if (!showTooltips) {
      return false;
    }

    return !!draftSettings?.showStepCounter;
  }, [showTooltips, draftSettings?.showStepCounter]);

  useEffect(() => {
    if (!draftSettings) {
      return;
    }

    replayerRef.current?.refreshTooltip();
  }, [draftSettings, framesWarnings]);

  const onImageChange = async (image: string) => {
    const imgPath = await uploadAnImage(
      image,
      Number.parseInt(tourId),
      fileServiceToken
    );
    updateDraftSettings('background', {
      ...draftSettings.background,
      image: imgPath,
      color: DEFAULT_BACKGROUND_COLOR,
    });
  };

  const backgroundUpdateTimeout = useRef<ReturnType<typeof setTimeout>>();

  const updateTourBackground = (color: EditToolColorValues) => {
    const background = {
      ...draftSettings.background,
      color,
      image: null,
      blur: false,
    };

    dispatch(
      setDraftSettings({
        uuid,
        draftSettings: {
          ...draftSettings,
          background,
        },
      })
    );

    if (backgroundUpdateTimeout.current) {
      clearTimeout(backgroundUpdateTimeout.current);
    }
    backgroundUpdateTimeout.current = setTimeout(() => {
      updateDraftSettings('background', background);
    }, 1000);
  };

  const [updateAllTourSteps] = useUpdateAllTourStepsMutation();

  const applyTourTheme = (theme: TourTheme) => {
    updateDraftSettings('tourThemeId', theme.id);
    const tooltipConfig = {
      config: {
        tooltip: theme.tooltipConfig,
      },
    };
    const updates = getObjectPropertyPathsWithValues(
      tooltipConfig,
      camelCaseToUnderscore
    );

    updateAllTourSteps({
      organizationId,
      tourId: Number(tourId),
      updates,
    });
    dispatch(
      setIsApplyingTourTheme({
        uuid,
        isApplyingTourTheme: true,
      })
    );
  };

  const [deleteTourThemeModalOpen, setDeleteTourThemeModalOpen] =
    useState(false);
  const [tourThemeForDeletion, setTourThemeForDeletion] =
    useState<TourTheme | null>(null);
  const [deleteTheme] = useDeleteTourThemeMutation();

  const openTourDeleteModal = (theme: TourTheme) => {
    setTourThemeForDeletion(theme);
    setDeleteTourThemeModalOpen(true);
  };
  const closeTourDeleteModal = () => {
    setTourThemeForDeletion(null);
    setDeleteTourThemeModalOpen(false);
  };
  const deleteTourTheme = () => {
    deleteTheme({
      organizationId,
      tourThemeId: tourThemeForDeletion.id,
    });
    closeTourDeleteModal();
  };

  return (
    <Drawer
      isOpen={isSettingsOpen}
      width={350}
      left
      header={
        <div className="flex p-0">
          <Button
            label="Back"
            link={true}
            dark
            leftIcon={<LeftOutlineIcon />}
            onClick={onSettingsClose}
            style={{ paddingLeft: 0 }}
          />
        </div>
      }
    >
      <div className="flex w-full flex-row align-items items-center px-5 py-4 -mt-4 border-b border-ds-natural-150">
        <div className="mr-2">
          <SettingsIcon
            size={20}
            color={ColorHex.natural600}
          />
        </div>
        <TextSubHeader
          label="Settings"
          color="text-ds-natural-600"
        />
      </div>

      <div className="px-5 py-3 bg-ds-natural-50">
        <TextBoxEditorTitle
          color="text-ds-natural-300"
          label="guidance"
          uppercase
        />
      </div>

      <div className="px-5 pb-5 pt-2.5">
        <EditToolToggle
          title="Menu navigation"
          onChange={() => {
            updateDraftSettings('navigationToggle', !navigationToggle);
          }}
          checked={navigationToggle}
          icon={<MapOutlineIcon size={17} />}
        />
        <div className="pl-6">
          <TextCaption
            label="Allow steps navigation with previous and next"
            color="text-ds-natural-300"
            tagName="p"
          />
        </div>
      </div>

      <Separator horizontal />

      <div className="px-5 py-3 bg-ds-natural-50">
        <TextBoxEditorTitle
          color="text-ds-natural-300"
          label="global appearance"
          uppercase
        />
      </div>

      {flags.toursOrgLevelThemes && (
        <>
          <div className="px-5 pb-5 pt-2.5">
            <TourThemePicker
              tourThemes={tourThemes}
              user={user}
              selectedThemeId={draftSettings?.tourThemeId}
              onThemeSelect={applyTourTheme}
              onThemeDelete={openTourDeleteModal}
            />
          </div>
          <Separator horizontal />
        </>
      )}
      <>
        <div className="px-5 pb-5 pt-2.5">
          <EditToolToggle
            title="Show tooltips"
            onChange={async () => {
              updateDraftSettings('showTooltips', !showTooltips).then(
                (data) => {
                  calculateAndStoreStepWarnings(data);
                }
              );
            }}
            checked={showTooltips}
            icon={<BalloonOutlineIcon size={17} />}
          />
          <div className="pl-6">
            <TextCaption
              label="Show tooltip on each step"
              color="text-ds-natural-300"
              tagName="p"
            />
          </div>
        </div>
        <Separator horizontal />
      </>
      <div className="px-5 pb-5 pt-2.5">
        <EditToolToggle
          title="Show step counter"
          onChange={() => {
            updateDraftSettings('showStepCounter', !showStepCounter);
          }}
          disabled={!showTooltips}
          checked={showStepCounter}
          icon={<TimerOutlineIcon size={17} />}
          hoverInfo={{
            type: 'tooltip',
            isVisible: !showTooltips,
            component: (
              <div className="w-ds-46">
                <TextCaption label="Step counter is available only with tooltips shown." />
              </div>
            ),
          }}
        />
        <div className="pl-6">
          <TextCaption
            label="Add step number to tooltip as current/total"
            color="text-ds-natural-300"
            tagName="p"
          />
        </div>
      </div>
      <Separator horizontal />
      {flags.toursSettingsAspectRatio && (
        <>
          <div className="px-5 pb-5 pt-2.5">
            <EditToolToggle
              hoverInfo={{
                type: 'tooltip',
                isVisible: isResponsivenessDisabled,
                component: (
                  <div className="w-ds-46">
                    <TextCaption label="When using image frames or the tour wasn't captured on full screen, auto responsive is disabled by default" />
                    <InlineLink
                      href="https://help.demostack.com/en/articles/6571567-customizing-a-product-tour#h_7df8ce77e5"
                      label="Learn more"
                      className="ml-1 inline-flex"
                      target="_blank"
                      color="text-ds-natural-300"
                      fontSize="text-ds-200"
                    />
                  </div>
                ),
              }}
              disabled={isResponsivenessDisabled}
              title="Auto responsive"
              onChange={() => {
                updateDraftSettings('responsiveness', !responsiveness);
              }}
              checked={responsiveness}
              icon={<LandscapeHorizontalAlignOutlineIcon size={17} />}
            />
            <div className="pl-6">
              <span>
                <TextCaption
                  color="text-ds-natural-300"
                  tagName="p"
                  label={
                    <>
                      <TextCaption label="Allow frame elements scale according to fit viewer's screen." />
                      <InlineLink
                        href="https://help.demostack.com/en/articles/6571567-customizing-a-product-tour#h_7df8ce77e5"
                        label="Learn more"
                        className="ml-1 inline-flex"
                        target="_blank"
                        color="text-ds-natural-300"
                        fontSize="text-ds-200"
                      />
                    </>
                  }
                />
              </span>
            </div>
          </div>
          <Separator horizontal />

          <>
            <div className="px-5 pb-5 pt-2.5">
              <CoverPicker
                color={
                  draftSettings?.background?.color || DEFAULT_BACKGROUND_COLOR
                }
                onColorChange={updateTourBackground}
                image={draftSettings?.background?.image}
                onImageChange={onImageChange}
                isImageBlurred={draftSettings?.background?.blur}
                setIsImageBlurred={(isBlurred) =>
                  updateDraftSettings('background', {
                    ...draftSettings.background,
                    blur: isBlurred,
                  })
                }
                hasCoverChanges={
                  !!draftSettings?.background?.color ||
                  !!draftSettings?.background?.image
                }
                onResetCoverChanges={() =>
                  updateDraftSettings('background', {
                    ...draftSettings.background,
                    backgroundType: null,
                    color: null,
                    image: null,
                    blur: null,
                  })
                }
                backgroundType={
                  draftSettings?.background?.backgroundType || 'color'
                }
                onBackgroundTypeChange={(backgroundType) => {
                  updateDraftSettings('background', {
                    ...draftSettings.background,
                    backgroundType,
                  });
                }}
              />
            </div>
            <Separator horizontal />
          </>
        </>
      )}
      {!responsiveness && (
        <>
          <div className="px-5 pb-5 pt-2.5">
            <EditToolToggle
              title={
                screenResolutionType === 'desktop'
                  ? 'Browser framer'
                  : 'Device / Browser appearance'
              }
              onChange={() => {
                const newWrapperType =
                  screenResolutionType === 'desktop' ? 'browser' : 'device';

                updateDraftSettings('background', {
                  ...draftSettings.background,
                  wrapperType: wrapperType ? null : newWrapperType,
                });
              }}
              checked={!!wrapperType}
              icon={<ResponsiveOutlineIcon size={17} />}
            />
            <div className="pl-6">
              <TextCaption
                label="Wrap your Tour with a Device or a Browser"
                color="text-ds-natural-300"
                tagName="p"
              />
              {wrapperType && screenResolutionType !== 'desktop' && (
                <div className="flex justify-start items-center pt-3">
                  <RadioGroupButton
                    buttonsConfig={[
                      {
                        id: 'device' as WrapperType,
                        label: 'Device',
                      },
                      {
                        id: 'browser' as WrapperType,
                        label: 'Browser',
                      },
                    ]}
                    onClick={(selected: WrapperType) => {
                      updateDraftSettings('background', {
                        ...draftSettings.background,
                        wrapperType: selected,
                      });
                    }}
                    selected={wrapperType}
                  />
                </div>
              )}
            </div>
          </div>
          <Separator horizontal />
        </>
      )}
      <DangerModal
        open={deleteTourThemeModalOpen}
        onClose={closeTourDeleteModal}
        onConfirm={deleteTourTheme}
        title="Are you sure you want to delete theme?"
        message={`Are you sure you want to delete "${tourThemeForDeletion?.name}" tour theme?`}
      />
    </Drawer>
  );
};

export default Settings;
